"use client"

import * as React from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  ChevronRight,
  Sparkles,
  Percent,
  Tag,
  Truck,
  CreditCard,
  HelpCircle,
  Package,
} from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useNavigationStore } from "@/store/use-navigation-store"

interface Category {
  id: string
  name: string
  slug: string
  children?: Category[]
  _count?: { products: number }
}

interface FeaturedProduct {
  id: string
  name: string
  image: string | null
  basePrice: number
}

const QUICK_LINKS = [
  {
    label: "Все двери",
    icon: Package,
    page: "catalog" as const,
    params: {},
  },
  {
    label: "Новинки",
    icon: Sparkles,
    page: "catalog" as const,
    params: { sort: "new" },
  },
  {
    label: "Акции",
    icon: Tag,
    page: "catalog" as const,
    params: { promo: "true" },
  },
  {
    label: "Распродажа",
    icon: Percent,
    page: "catalog" as const,
    params: { sale: "true" },
  },
  {
    label: "Доставка",
    icon: Truck,
    page: "delivery" as const,
    params: {},
  },
  {
    label: "Оплата",
    icon: CreditCard,
    page: "delivery" as const,
    params: {},
  },
  {
    label: "FAQ",
    icon: HelpCircle,
    page: "faq" as const,
    params: {},
  },
]

export function MegaMenu() {
  const navigate = useNavigationStore((s) => s.navigate)
  const currentPage = useNavigationStore((s) => s.page)
  const [open, setOpen] = React.useState(false)
  const [categories, setCategories] = React.useState<Category[]>([])
  const [featuredProducts, setFeaturedProducts] = React.useState<FeaturedProduct[]>([])
  const [loading, setLoading] = React.useState(true)
  const hoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fetch categories and featured products
  React.useEffect(() => {
    async function fetchData() {
      try {
        const [catRes, prodRes] = await Promise.all([
          fetch("/api/categories"),
          fetch("/api/products?featured=true&limit=4"),
        ])
        const catData = await catRes.json()
        const prodData = await prodRes.json()
        setCategories(Array.isArray(catData) ? catData : [])
        setFeaturedProducts(prodData?.products?.slice(0, 4) || [])
      } catch {
        // Silently fail - menu still works without data
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  // Close on scroll
  React.useEffect(() => {
    const handleScroll = () => setOpen(false)
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Close on page change
  React.useEffect(() => {
    setOpen(false)
  }, [currentPage])

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    }
  }, [])

  const handleMouseEnter = React.useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    setOpen(true)
  }, [])

  const handleMouseLeave = React.useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => setOpen(false), 150)
  }, [])

  const handleCategoryClick = React.useCallback(
    (categoryId: string) => {
      navigate("catalog", { categoryId })
      setOpen(false)
    },
    [navigate]
  )

  const handleProductClick = React.useCallback(
    (productId: string) => {
      navigate("product", { id: productId })
      setOpen(false)
    },
    [navigate]
  )

  const handleQuickLink = React.useCallback(
    (page: string, params: Record<string, string>) => {
      navigate(page, params)
      setOpen(false)
    },
    [navigate]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-sm font-normal nav-link-underline"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          Каталог
        </Button>
      </PopoverTrigger>

      <AnimatePresence>
        {open && (
          <PopoverContent
            className="w-[600px] p-0 overflow-hidden"
            side="bottom"
            align="start"
            sideOffset={4}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            asChild
            forceMount
          >
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="w-[600px] bg-popover text-popover-foreground rounded-md border shadow-lg"
            >
              <div className="grid grid-cols-[1fr_1fr_180px] divide-x">
                {/* Left Column: Categories */}
                <div className="p-4 max-h-[400px] overflow-y-auto">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Категории
                  </h3>
                  {loading ? (
                    <div className="space-y-2">
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-6 w-full" />
                      ))}
                    </div>
                  ) : categories.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Категории не найдены
                    </p>
                  ) : (
                    <nav className="space-y-1">
                      {categories.map((cat) => (
                        <div key={cat.id}>
                          <button
                            onClick={() => handleCategoryClick(cat.id)}
                            className="flex items-center justify-between w-full px-2 py-1.5 text-sm font-medium rounded hover:bg-accent hover:text-accent-foreground transition-colors text-left group"
                          >
                            <span>{cat.name}</span>
                            {cat._count && cat._count.products > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {cat._count.products}
                              </span>
                            )}
                          </button>
                          {cat.children && cat.children.length > 0 && (
                            <div className="ml-3 space-y-0.5">
                              {cat.children.map((sub) => (
                                <button
                                  key={sub.id}
                                  onClick={() => handleCategoryClick(sub.id)}
                                  className="flex items-center gap-1 w-full px-2 py-1 text-sm text-muted-foreground rounded hover:bg-accent hover:text-accent-foreground transition-colors text-left"
                                >
                                  <ChevronRight className="size-3 shrink-0" />
                                  <span className="truncate">{sub.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </nav>
                  )}
                </div>

                {/* Middle Column: Featured Products */}
                <div className="p-4 max-h-[400px] overflow-y-auto">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Популярные товары
                  </h3>
                  {loading ? (
                    <div className="space-y-3">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex gap-3">
                          <Skeleton className="size-14 rounded-md shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-4 w-16" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : featuredProducts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Нет товаров
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {featuredProducts.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleProductClick(product.id)}
                          className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-accent transition-colors text-left group"
                        >
                          <div className="size-14 rounded-md overflow-hidden bg-muted shrink-0">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="size-full object-cover group-hover:scale-105 transition-transform"
                              />
                            ) : (
                              <div className="size-full flex items-center justify-center">
                                <Package className="size-5 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                              {product.name}
                            </p>
                            <p className="text-sm font-semibold text-primary mt-0.5">
                              {product.basePrice.toLocaleString("ru-RU")} ₽
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Column: Quick Links */}
                <div className="p-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Быстрые ссылки
                  </h3>
                  <nav className="space-y-0.5">
                    {QUICK_LINKS.map((link) => {
                      const Icon = link.icon
                      return (
                        <button
                          key={link.label}
                          onClick={() =>
                            handleQuickLink(link.page, link.params)
                          }
                          className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground transition-colors text-left"
                        >
                          <Icon className="size-4 text-muted-foreground" />
                          <span>{link.label}</span>
                        </button>
                      )
                    })}
                  </nav>
                </div>
              </div>
            </motion.div>
          </PopoverContent>
        )}
      </AnimatePresence>
    </Popover>
  )
}
